import { ConfirmationModal, useModals } from "@skailer-com/uikit";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useBeforeUnload, useBlocker } from "react-router-dom";

export const useUnsavedChangesBlocker = (hasUnsavedChanges: boolean) => {
  const { openModal } = useModals();
  const { t } = useTranslation();
  const allowNavigationRef = useRef(false);
  const blockerModalIsOpenRef = useRef(false);
  const resetBlockerOnModalCloseRef = useRef(true);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      allowNavigationRef.current = false;
    }
  }, [hasUnsavedChanges]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !allowNavigationRef.current &&
      hasUnsavedChanges &&
      currentLocation.pathname !== nextLocation.pathname
  );

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!hasUnsavedChanges) return;

        event.preventDefault();
      },
      [hasUnsavedChanges]
    )
  );

  useEffect(() => {
    if (blocker.state !== "blocked" || blockerModalIsOpenRef.current) return;

    blockerModalIsOpenRef.current = true;
    resetBlockerOnModalCloseRef.current = true;

    openModal(ConfirmationModal, {
      question: t("common:unsaved_changes_may_be_lost_title"),
      text: t("common:unsaved_changes_may_be_lost_description"),
      acceptTitle: t("common:leave"),
      secondButtonTitle: t("common:stay"),
      onAccept: async () => {
        resetBlockerOnModalCloseRef.current = false;
        blocker.proceed();
      },
      onClose: () => {
        blockerModalIsOpenRef.current = false;

        if (resetBlockerOnModalCloseRef.current) {
          blocker.reset();
        }
      },
    });
  }, [blocker]);

  const navigateWithoutBlocking = useCallback((navigate: () => void) => {
    allowNavigationRef.current = true;
    navigate();
  }, []);

  return { navigateWithoutBlocking };
};
